import type { Row, Transaction } from "@rocicorp/zero";
import { zql, type Schema } from "../../zero/schema";

/**
 * Scope boundary for resolving owner context and current write eligibility.
 *
 * This starts intentionally small: enough to centralize the current personal
 * user and shared space scope checks used by scope-owned domains.
 */
export namespace Scope {
  type AnyInfo =
    | { kind: "user"; scopeId: string; userId: string }
    | { kind: "space"; scopeId: string; groupId: string }
    | { kind: "mealPlanningSession"; scopeId: string; mealPlanningSessionId: string };

  export type Kind = AnyInfo["kind"];
  export type Info<K extends Kind = Kind> = Extract<AnyInfo, { kind: K }>;

  export async function byId<K extends Kind = Kind>(
    tx: Transaction,
    scopeId: string,
    kinds?: readonly K[],
  ): Promise<Info<K>> {
    const scope = await tx.run(zql.scope.where("id", "=", scopeId).one());
    if (!scope) {
      throw new Error("Scope not found");
    }
    const info = asInfo(scope);
    if (kinds && !(kinds as readonly Kind[]).includes(info.kind)) {
      throw new Error("Scope has unexpected kind");
    }
    return info as Info<K>;
  }

  export function asInfo(scope: Row<Schema["tables"]["scope"]>): Info {
    if (scope.userId) {
      return { kind: "user", scopeId: scope.id, userId: scope.userId };
    }
    if (scope.spaceId) {
      return { kind: "space", scopeId: scope.id, groupId: scope.spaceId };
    }
    if (scope.mealPlanningSessionId) {
      return {
        kind: "mealPlanningSession",
        scopeId: scope.id,
        mealPlanningSessionId: scope.mealPlanningSessionId,
      };
    }

    throw new Error("Invalid scope");
  }

  export async function create(tx: Transaction, args: { kind: Kind; id: string }): Promise<void> {
    await tx.mutate.scope.insert({
      id: args.id,
      userId: args.kind === "user" ? args.id : null,
      spaceId: args.kind === "space" ? args.id : null,
      mealPlanningSessionId: args.kind === "mealPlanningSession" ? args.id : null,
    });
  }

  export async function deleteById(tx: Transaction, scopeId: string): Promise<void> {
    await tx.mutate.scope.delete({ id: scopeId });
  }
}
